import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import isEqual from 'lodash/isEqual';

// Constants for location matching
const LOCATION_MATCH = {
  EXACT: 'exact',
  SIMILAR: 'similar',
  NEARBY: 'nearby',
  NONE: 'none'
};

const TYPE_PRIORITIES = {
  capital: 1,
  city: 2,
  settlement: 3,
  epicentre: 4,
  default: 99
};

// Helper function to calculate distance between two points in kilometers
const calculateDistance = (lon1, lat1, lon2, lat2) => {
  const toRad = (value) => value * Math.PI / 180;
  const R = 6371; // Earth's radius in km
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * 
    Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c;
};

// Helper function to normalize location names
const normalizeName = (name) => {
  if (!name) return '';
  return name.toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Remove diacritics
    .replace(/[^a-z0-9\s]/g, '') // Remove special characters
    .trim();
};

// Helper function to calculate string similarity
const calculateStringSimilarity = (str1, str2) => {
  const s1 = normalizeName(str1);
  const s2 = normalizeName(str2);
  
  if (!s1 || !s2) return 0;
  if (s1 === s2) return 1;
  
  let longer = s1.length > s2.length ? s1 : s2;
  let shorter = s1.length > s2.length ? s2 : s1;
  
  const longerLength = longer.length;
  if (longerLength === 0) return 1.0;
  
  const editDistance = (s1, s2) => {
    const costs = [];
    for (let i = 0; i <= s1.length; i++) {
      let lastValue = i;
      for (let j = 0; j <= s2.length; j++) {
        if (i === 0) {
          costs[j] = j;
        } else if (j > 0) {
          let newValue = costs[j - 1];
          if (s1.charAt(i - 1) !== s2.charAt(j - 1)) {
            newValue = Math.min(Math.min(newValue, lastValue), costs[j]) + 1;
          }
          costs[j - 1] = lastValue;
          lastValue = newValue;
        }
      }
      if (i > 0) costs[s2.length] = lastValue;
    }
    return costs[s2.length];
  };

  return (longerLength - editDistance(longer, shorter)) / longerLength;
};

// Helper function to determine if two locations match
const getLocationMatch = (loc1, loc2, options = {}) => {
  const {
    nameSimilarityThreshold = 0.8,
    distanceThreshold = 5, // km
    strictTypeMatch = false
  } = options;

  // Check if types are compatible
  const type1 = loc1.type?.toLowerCase() || 'default';
  const type2 = loc2.type?.toLowerCase() || 'default';
  
  if (strictTypeMatch && type1 !== type2) {
    return LOCATION_MATCH.NONE;
  }

  // Special handling for capitals - they should only match with other capitals
  if ((type1 === 'capital' || type2 === 'capital') && type1 !== type2) {
    return LOCATION_MATCH.NONE;
  }

  // Check for exact name match
  const name1 = normalizeName(loc1.name);
  const name2 = normalizeName(loc2.name);
  
  if (name1 && name2 && name1 === name2) {
    return LOCATION_MATCH.EXACT;
  }

  // Check for similar names
  if (name1 && name2) {
    const similarity = calculateStringSimilarity(name1, name2);
    if (similarity >= nameSimilarityThreshold) {
      return LOCATION_MATCH.SIMILAR;
    }
  }

  // Check for nearby coordinates
  const lon1 = parseFloat(loc1.lon);
  const lat1 = parseFloat(loc1.lat);
  const lon2 = parseFloat(loc2.lon);
  const lat2 = parseFloat(loc2.lat);

  if (!isNaN(lon1) && !isNaN(lat1) && !isNaN(lon2) && !isNaN(lat2)) {
    const distance = calculateDistance(lon1, lat1, lon2, lat2);
    if (distance <= distanceThreshold) {
      return LOCATION_MATCH.NEARBY;
    }
  }

  return LOCATION_MATCH.NONE;
};

export const useMapLocations = (viewIso3, mapAnalysisData) => {
  // Separate states for database and analysis locations
  const [dbLocations, setDbLocations] = useState([]);
  const [analysisLocations, setAnalysisLocations] = useState([]);
  const [isDbLoading, setIsDbLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Cache to prevent unnecessary re-fetching of database locations
  const dbLocationsCache = useRef(new Map());
  const previousAnalysisLocationsRef = useRef([]);

  // Memoize the analysis locations parsing - this can change with pillar changes
  const parsedAnalysisLocations = useMemo(() => {
    if (!mapAnalysisData?.content) {
      return [];
    }
    try {
      const parsedContent = JSON.parse(mapAnalysisData.content);
      return parsedContent.locations || [];
    } catch (e) {
      return [];
    }
  }, [mapAnalysisData?.content]);

  // Function to convert a location to a GeoJSON feature
  const locationToFeature = useCallback((location, source = 'db') => {
    if (!location) {
      return null;
    }

    // Handle both GeoJSON and raw location formats
    const isGeoJSON = location.type === 'Feature' && location.properties;
    const name = isGeoJSON ? location.properties.name_en : location.name;
    const type = isGeoJSON ? location.properties.type : location.type;
    const coordinates = isGeoJSON ? location.geometry.coordinates : 
      (source === 'db' ? location.coordinates : [location.lon, location.lat]);

    // Normalize location name
    const normalizedName = name?.trim();
    if (!normalizedName) {
      return null;
    }

    // Validate coordinates
    if (!Array.isArray(coordinates) || coordinates.length !== 2) {
      return null;
    }

    const [lon, lat] = coordinates;
    if (typeof lon !== 'number' || typeof lat !== 'number' || isNaN(lon) || isNaN(lat)) {
      return null;
    }

    // For analysis locations, check if it's trying to override the capital
    if (source === 'analysis' && 
        normalizedName.toLowerCase().replace(/\s+/g, '') === 'naypyitaw') {
      return null;
    }

    // **CRITICAL FIX**: Analysis locations should NEVER be capitals!
    // Only database locations (from symbol_points table) can be capitals
    let finalType = type || 'city';
    let isCapital = false;
    
    if (source === 'analysis') {
      // For analysis locations, force type to be city/settlement at most, never capital
      if (finalType === 'capital') {
        finalType = 'city'; // Convert capitals to cities for analysis locations
      }
      isCapital = false; // Analysis locations are NEVER capitals
    } else {
      // For database locations, preserve the original type logic
      isCapital = finalType === 'capital';
    }

    // Create the feature
    const feature = {
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [lon, lat]
      },
      properties: {
        name_en: normalizedName,
        type: finalType,
        lat,
        lon,
        source,
        view_id: location.view_id,
        is_capital: isCapital,
        properties: location.properties || {}
      }
    };

    return feature;
  }, []);

  // Function to merge database locations with analysis locations
  const mergeLocations = useCallback((dbLocs, analysisLocs) => {
    const mergedLocations = new Map();

    // First, add all database locations (these are persistent)
    dbLocs.forEach(location => {
      if (!location.geometry || !location.properties) return;

      const [lon, lat] = location.geometry.coordinates;
      if (typeof lon !== 'number' || typeof lat !== 'number' || isNaN(lon) || isNaN(lat)) return;

      const feature = {
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [lon, lat]
        },
        properties: {
          name_en: location.properties.name_en,
          type: location.properties.type || 'city',
          lat,
          lon,
          source: 'db',
          view_id: location.properties.view_id,
          is_capital: location.properties.is_capital,
          properties: location.properties.properties || {}
        }
      };

      const key = `${feature.properties.name_en}|${feature.properties.type}`;
      mergedLocations.set(key, feature);
    });

    // Then, add analysis locations that don't conflict with database locations
    if (analysisLocs && analysisLocs.length > 0) {
      analysisLocs.forEach(location => {
        const analysisFeature = locationToFeature(location, 'analysis');
        if (!analysisFeature) return;

        // Check if we have a DB location with the same name
        const dbKey = `${analysisFeature.properties.name_en}|${analysisFeature.properties.type}`;
        if (mergedLocations.has(dbKey)) {
          return; // Skip if database location already exists
        }

        // Add the analysis location
        const key = `${analysisFeature.properties.name_en}|${analysisFeature.properties.type}`;
        mergedLocations.set(key, analysisFeature);
      });
    }

    // Convert to array and sort by type priority
    return Array.from(mergedLocations.values())
      .sort((a, b) => {
        // First sort by source (db before analysis)
        if (a.properties.source !== b.properties.source) {
          return a.properties.source === 'db' ? -1 : 1;
        }
        // Then by type priority (capitals first)
        if (a.properties.is_capital) return -1;
        if (b.properties.is_capital) return 1;
        
        const priorityA = TYPE_PRIORITIES[a.properties.type] || TYPE_PRIORITIES.default;
        const priorityB = TYPE_PRIORITIES[b.properties.type] || TYPE_PRIORITIES.default;
        return priorityA - priorityB;
      });
  }, [locationToFeature]);

  // Fetch database symbol points (only when view changes, not pillar)
  useEffect(() => {
    const fetchDbSymbolPoints = async () => {
      if (!viewIso3) {
        setDbLocations([]);
        setIsDbLoading(false);
        return;
      }

      // Check cache first
      const cached = dbLocationsCache.current.get(viewIso3);
      if (cached) {
        setDbLocations(cached);
        setIsDbLoading(false);
        return;
      }

      try {
        setIsDbLoading(true);
        
        const response = await fetch(`/api/views/${viewIso3}/symbol-points`);
        
        if (!response.ok) {
          throw new Error(`Failed to fetch symbol points: ${response.status}`);
        }

        const locations = await response.json();
        
        // Cache the result
        dbLocationsCache.current.set(viewIso3, locations);
        setDbLocations(locations);
        setError(null);
      } catch (err) {
        setError(err.message);
        setDbLocations([]);
      } finally {
        setIsDbLoading(false);
      }
    };

    fetchDbSymbolPoints();
  }, [viewIso3]); // Only depends on viewIso3, NOT on mapAnalysisData

  // Process analysis locations (can change with pillar)
  useEffect(() => {
    // Only update if analysis locations have actually changed
    if (!isEqual(parsedAnalysisLocations, previousAnalysisLocationsRef.current)) {
      previousAnalysisLocationsRef.current = parsedAnalysisLocations;
      setAnalysisLocations(parsedAnalysisLocations);
    }
  }, [parsedAnalysisLocations]);

  // Combine database and analysis locations
  const combinedLocations = useMemo(() => {
    return mergeLocations(dbLocations, analysisLocations);
  }, [dbLocations, analysisLocations, mergeLocations]);

  // Memoize the return value to prevent unnecessary re-renders
  return useMemo(() => ({
    locations: combinedLocations,
    isLoading: isDbLoading, // Only show loading for database locations
    error
  }), [combinedLocations, isDbLoading, error]);
}; 